import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form, Input, Typography, Checkbox, Select } from '@crisellevaldez/speculo-ui';
import { Plus, X, AlertCircle } from 'lucide-react';
import { mlsSetupSchema, MLSSetupData, mlsEntrySchema } from '../schemas/onboarding.schema';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { InfoNotice } from '../components/InfoNotice';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { useApprovedMLSList } from '../../../../hooks/client/mls/useApprovedMLSList';

interface MLSSetupStepProps {
  onNext: (data: MLSSetupData) => void;
  initialData?: Partial<MLSSetupData>;
  isLoading?: boolean;
  userEmail?: string;
  userName?: string;
  idxSubdomain?: string;
}

interface MLSEntry {
  mls_name: string;
  brokerage_name: string;
  broker_full_name: string;
  broker_email: string;
  agent_name: string;
  agent_email: string;
  agent_mls_id?: string;
}

export const MLSSetupStep: React.FC<MLSSetupStepProps> = ({
  onNext,
  initialData,
  isLoading = false,
  userEmail = '',
  userName = '',
  idxSubdomain = '',
}) => {
  const { data: approvedMLSList = [], isLoading: isLoadingMLS } = useApprovedMLSList();

  // Convert MLS list to select options - show name with board code
  const mlsOptions = useMemo(() => {
    return approvedMLSList.map((mls) => ({
      value: mls.boardCode,
      label: mls.mlsName ? `${mls.mlsName} (${mls.boardCode})` : mls.boardCode,
    }));
  }, [approvedMLSList]);

  const [mlsEntries, setMlsEntries] = useState<MLSEntry[]>(
    initialData?.mls_entries || [
      {
        mls_name: '',
        brokerage_name: '',
        broker_full_name: '',
        broker_email: '',
        agent_name: '',
        agent_email: '',
        agent_mls_id: '',
      },
    ]
  );

  const [currentEntryIndex, setCurrentEntryIndex] = useState(0);
  const [agentNameSameAsBroker, setAgentNameSameAsBroker] = useState(false);
  const [agentNameSameAsUser, setAgentNameSameAsUser] = useState(false);
  const [agentEmailSameAsBroker, setAgentEmailSameAsBroker] = useState(false);
  const [agentEmailSameAsUser, setAgentEmailSameAsUser] = useState(false);

  const form = useForm<MLSEntry>({
    resolver: zodResolver(mlsEntrySchema),
    defaultValues: mlsEntries[currentEntryIndex],
    mode: 'onBlur',
  });

  const handleAddAnotherMLS = async () => {
    // Trigger validation on the current form
    const isValid = await form.trigger();

    if (!isValid) {
      // Don't add new entry if current entry has validation errors
      return;
    }

    // Save current entry data first
    const currentData = form.getValues();
    const updatedEntries = [...mlsEntries];
    updatedEntries[currentEntryIndex] = currentData;

    // Add new empty entry
    const newEntry: MLSEntry = {
      mls_name: '',
      brokerage_name: '',
      broker_full_name: '',
      broker_email: '',
      agent_name: '',
      agent_email: '',
      agent_mls_id: '',
    };

    const newEntries = [...updatedEntries, newEntry];
    setMlsEntries(newEntries);
    setCurrentEntryIndex(newEntries.length - 1);
    form.reset(newEntry);
    setAgentNameSameAsBroker(false);
    setAgentNameSameAsUser(false);
    setAgentEmailSameAsBroker(false);
    setAgentEmailSameAsUser(false);
  };

  const handleRemoveEntry = (index: number) => {
    const updatedEntries = mlsEntries.filter((_, i) => i !== index);
    setMlsEntries(updatedEntries);

    if (currentEntryIndex >= updatedEntries.length) {
      setCurrentEntryIndex(Math.max(0, updatedEntries.length - 1));
    }

    if (updatedEntries.length > 0) {
      form.reset(
        updatedEntries[
          currentEntryIndex >= updatedEntries.length ? updatedEntries.length - 1 : currentEntryIndex
        ]
      );
    }
  };

  const handleCurrentEntrySubmit = async (currentEntryData: MLSEntry) => {
    // Update current entry with validated data
    const updatedEntries = [...mlsEntries];
    updatedEntries[currentEntryIndex] = currentEntryData;
    setMlsEntries(updatedEntries);

    // Filter out incomplete entries (only keep entries where ALL required fields are filled)
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const filledEntries = updatedEntries.filter((entry) => {
      return (
        entry.mls_name &&
        entry.mls_name.trim() !== '' &&
        entry.brokerage_name &&
        entry.brokerage_name.trim() !== '' &&
        entry.broker_full_name &&
        entry.broker_full_name.trim() !== '' &&
        entry.broker_email &&
        entry.broker_email.trim() !== '' &&
        emailPattern.test(entry.broker_email) &&
        entry.agent_name &&
        entry.agent_name.trim() !== '' &&
        entry.agent_email &&
        entry.agent_email.trim() !== '' &&
        emailPattern.test(entry.agent_email) &&
        entry.agent_mls_id &&
        entry.agent_mls_id.trim() !== ''
      );
    });

    // Validate filled entries
    try {
      const validatedData = mlsSetupSchema.parse({ mls_entries: filledEntries });
      onNext(validatedData);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleAgentNameChange = (type: 'broker' | 'user') => {
    if (type === 'broker') {
      setAgentNameSameAsBroker(!agentNameSameAsBroker);
      setAgentNameSameAsUser(false);
      if (!agentNameSameAsBroker) {
        const brokerName = form.getValues('broker_full_name');
        form.setValue('agent_name', brokerName);
      }
    } else {
      setAgentNameSameAsUser(!agentNameSameAsUser);
      setAgentNameSameAsBroker(false);
      if (!agentNameSameAsUser) {
        form.setValue('agent_name', userName);
      }
    }
  };

  const handleAgentEmailChange = (type: 'broker' | 'user') => {
    if (type === 'broker') {
      setAgentEmailSameAsBroker(!agentEmailSameAsBroker);
      setAgentEmailSameAsUser(false);
      if (!agentEmailSameAsBroker) {
        const brokerEmail = form.getValues('broker_email');
        form.setValue('agent_email', brokerEmail);
      }
    } else {
      setAgentEmailSameAsUser(!agentEmailSameAsUser);
      setAgentEmailSameAsBroker(false);
      if (!agentEmailSameAsUser) {
        form.setValue('agent_email', userEmail);
      }
    }
  };

  return (
    <StageContainer
      title="IDX Licensing Agreement & MLS Approval"
      description="IDX allows Remi to engage in contextual conversations using real property data, as well as your website to display MLS listings in compliance with local regulation."
    >
      <div className="space-y-8">
        {/* Intro Notice */}
        <InfoNotice>
          To begin the IDX licensing agreement process, we need to verify your credentials and
          broker authorization.
        </InfoNotice>

        {/* MLS Entry Navigation */}
        {mlsEntries.length > 1 && (
          <div className="flex flex-wrap gap-2 items-center">
            <p className="text-sm font-semibold text-gray-700">MLS Entries:</p>
            {mlsEntries.map((entry, index) => (
              <div key={index} className="flex items-center gap-1">
                <Button
                  type="button"
                  variant={currentEntryIndex === index ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => {
                    // Save current entry before switching
                    const currentData = form.getValues();
                    const updatedEntries = [...mlsEntries];
                    updatedEntries[currentEntryIndex] = currentData;
                    setMlsEntries(updatedEntries);

                    // Switch to selected entry
                    setCurrentEntryIndex(index);
                    form.reset(updatedEntries[index]);
                  }}
                  className="inline-flex items-center gap-1"
                >
                  MLS {index + 1}
                </Button>
                {index > 0 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveEntry(index)}
                    className="text-red-600 hover:text-red-800 p-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* MLS Entry Form */}
        <form
          onSubmit={form.handleSubmit(handleCurrentEntrySubmit)}
          className="space-y-6"
          noValidate
        >
          <FormSection>
            <Form.Control required>
              <QuestionLabel>MLS</QuestionLabel>
              <Controller
                control={form.control}
                name="mls_name"
                render={({ field }) => (
                  <Select
                    {...field}
                    options={mlsOptions}
                    placeholder={isLoadingMLS ? 'Loading MLS options...' : 'Search and select MLS'}
                    error={form.formState.errors.mls_name?.message}
                    searchable
                    disabled={isLoadingMLS}
                  />
                )}
              />
              <TipBox>
                Search and select your MLS board/association name. This ensures we receive the
                correct board/MLS information.
              </TipBox>
            </Form.Control>
          </FormSection>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Brokerage Name</QuestionLabel>
              <Input
                {...form.register('brokerage_name')}
                placeholder="Enter brokerage name"
                error={form.formState.errors.brokerage_name?.message}
              />
            </Form.Control>
          </FormSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSection>
              <Form.Control required>
                <QuestionLabel>Broker Full Name</QuestionLabel>
                <Input
                  {...form.register('broker_full_name')}
                  placeholder="Enter broker full name"
                  error={form.formState.errors.broker_full_name?.message}
                />
                <TipBox>Will need to sign/approve the IDX licensing agreement</TipBox>
              </Form.Control>
            </FormSection>

            <FormSection>
              <Form.Control required>
                <QuestionLabel>Broker Email</QuestionLabel>
                <Input
                  {...form.register('broker_email')}
                  type="email"
                  placeholder="broker@example.com"
                  error={form.formState.errors.broker_email?.message}
                />
              </Form.Control>
            </FormSection>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormSection>
              <Form.Control required>
                <QuestionLabel>Agent Name</QuestionLabel>
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <Checkbox
                      id={`agent-name-same-as-broker-${currentEntryIndex}`}
                      checked={agentNameSameAsBroker}
                      onChange={() => handleAgentNameChange('broker')}
                      label="Same as broker"
                      disabled={!form.watch('broker_full_name')}
                    />
                    <Checkbox
                      id={`agent-name-same-as-user-${currentEntryIndex}`}
                      checked={agentNameSameAsUser}
                      onChange={() => handleAgentNameChange('user')}
                      label="Same as me"
                    />
                  </div>
                  <Input
                    {...form.register('agent_name')}
                    placeholder="Enter agent name"
                    error={form.formState.errors.agent_name?.message}
                    // Read-only, not disabled: react-hook-form drops disabled inputs on submit.
                    readOnly={agentNameSameAsBroker || agentNameSameAsUser}
                    className={
                      agentNameSameAsBroker || agentNameSameAsUser
                        ? 'bg-gray-100 dark:bg-white/5'
                        : ''
                    }
                  />
                </div>
                <TipBox>Must be an active member of this MLS</TipBox>
              </Form.Control>
            </FormSection>

            <FormSection>
              <Form.Control required>
                <QuestionLabel>Agent Email</QuestionLabel>
                <div className="space-y-3">
                  <div className="flex gap-4">
                    <Checkbox
                      id={`same-as-broker-${currentEntryIndex}`}
                      checked={agentEmailSameAsBroker}
                      onChange={() => handleAgentEmailChange('broker')}
                      label="Same as broker"
                      disabled={!form.watch('broker_email')}
                    />
                    <Checkbox
                      id={`same-as-user-${currentEntryIndex}`}
                      checked={agentEmailSameAsUser}
                      onChange={() => handleAgentEmailChange('user')}
                      label="Same as me"
                    />
                  </div>
                  <Input
                    {...form.register('agent_email')}
                    type="email"
                    placeholder="agent@example.com"
                    error={form.formState.errors.agent_email?.message}
                    readOnly={agentEmailSameAsBroker || agentEmailSameAsUser}
                    className={
                      agentEmailSameAsBroker || agentEmailSameAsUser
                        ? 'bg-gray-100 dark:bg-white/5'
                        : ''
                    }
                  />
                </div>
              </Form.Control>
            </FormSection>
          </div>

          <FormSection>
            <Form.Control required>
              <QuestionLabel>Agent MLS ID</QuestionLabel>
              <Input
                {...form.register('agent_mls_id')}
                placeholder="Enter agent MLS ID"
                error={form.formState.errors.agent_mls_id?.message}
              />
            </Form.Control>
          </FormSection>

          {/* MLS License Note */}
          <InfoNotice>
            The number of MLS licenses included with your current subscription is limited. Adding
            any licenses beyond your included access may result in additional fees.
          </InfoNotice>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleAddAnotherMLS}
              className="inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Additional MLS
            </Button>
          </div>
        </form>

        {/* Disclaimer */}
        <FormSection variant="primary" className="space-y-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-3">
              <Typography variant="h3" className="text-lg font-semibold text-yellow-900">
                Important Notice Regarding MLS Integration & Approval
              </Typography>

              <div className="text-sm text-yellow-900 space-y-3">
                <p>Please review the following regarding the IDX setup for Remi:</p>

                <div className="space-y-2">
                  <p className="font-semibold">Varied Timelines:</p>
                  <p>
                    Every MLS operates differently. IDX processes and processing timelines vary
                    significantly by MLS association.
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold">Agreement Process:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Manual Agreements:</strong> If your MLS requires digital signatures,
                      agreements will be emailed to you and your broker at the addresses provided
                      above.
                    </li>
                    <li>
                      <strong>Automatic Approval:</strong> If your MLS supports automatic approval,
                      no separate agreement document is necessary, provided you are in good standing
                      with your association.
                    </li>
                  </ul>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold">Timing:</p>
                  <p>
                    Submitting this form does not instantly trigger the agreement process. Please
                    allow 24–72 hours after submission for agreements to be generated and sent to
                    you.
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold">Fees & Requirements:</p>
                  <p>
                    Additional steps or documentation may be required depending on your specific MLS
                    rules. Additionally, pass-through fees may apply if your MLS charges for data
                    access.
                  </p>
                </div>

                <div className="space-y-2">
                  <p className="font-semibold">Next Steps:</p>
                  <p>
                    The Speculo Team will notify you once your agreement is processed. Please
                    monitor your email closely for all MLS communications. Final MLS approval is
                    highly recommended to launch Remi.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </FormSection>

        {/* Action Buttons */}
        <div className="flex justify-end">
          <NextButton
            type="submit"
            onClick={() => form.handleSubmit(handleCurrentEntrySubmit)()}
            isLoading={isLoading}
          />
        </div>
      </div>
    </StageContainer>
  );
};
